/* Los cortes del RTO, en un solo lugar.
 *
 * POR QUÉ ES UN MÓDULO COMPARTIDO. La misma pregunta —¿está vencido, le falta
 * poco, está bien?— se hace en tres pantallas: el RTO de tractores, el de
 * furgones y las tarjetas de novedades. Con la regla escrita en cada una, el
 * día que alguien mueva el corte de 30 días lo mueve en un lado y las otras dos
 * siguen contestando distinto sobre el mismo camión. Ya pasó con `DIAS_VIEJO`,
 * que por eso vive en el service y no en la pantalla.
 *
 * Y es puro: entra una fila, sale un grupo. Se prueba en una tabla.
 */

import { mediodiaHoyAr } from "./fechaAr";

/* Con menos de esto no se espera al vencimiento: se renueva antes de que el
 * camión salga. Es la regla que puso portería, y es la misma que usa
 * `shared/documentacion.js` para los papeles del chofer — un mes es lo que
 * tarda en conseguirse un turno. */
export const DIAS_AVISO = 30;

/* Vencido hace más de esto ya no es una tarea de esta semana: son unidades que
 * en su enorme mayoría no trabajan y que el padrón sigue dando por activas.
 * Se comparte con `novedades` para que las dos digan el mismo número. */
export const DIAS_VIEJO = 90;

/* ── «SIN USO?»: VENCIDA Y HACE RATO QUE NADIE LA VE ───────────────────────
 *
 * Pedido de Igna, 30/08: «si tiene el RTO vencido y la última ubicación
 * registrada es hace más de 60 días, que no aparezca como Vencido porque ya no
 * lo están usando. Creemos un tag Sin uso? y que después Nico o alguien lo vaya
 * a ver y nos diga qué onda».
 *
 * Es un grupo aparte y NO una baja: nadie declaró nada. Es una PREGUNTA —por eso
 * lleva el signo— y el que la contesta es una persona que va a mirar.
 *
 * POR QUÉ SACA FILAS DE «VENCIDO» Y ESO ESTÁ BIEN. De 429 vencidas, 183 caen
 * acá. Esa lista dejó de ser accionable hace rato justamente por eso: el que la
 * abre el lunes no puede distinguir el camión que salió ayer con la revisión
 * vencida —que es una urgencia— del acoplado que está contra el alambrado desde
 * 2019. Mezclados, los dos se leen igual y no se hace ninguno de los dos.
 *
 * ── LO QUE NO SE SABE NO ENTRA ────────────────────────────────────────────
 *
 * Sin registro de actividad la unidad SIGUE SIENDO «Vencido» —son 210, casi la
 * mitad—. «No la vimos nunca» no es «no se usa»: un acoplado sin equipo que se
 * mueve sin que nadie cargue el viaje no aparece en ninguna de las tres
 * fuentes, y marcarlo «Sin uso?» sería declarar por él. El error cae siempre
 * para el mismo lado: ante la duda queda vencida, que es lo que hace que
 * alguien la mire.
 *
 * ── Y EL PORTÓN NO SE ENTERA DE ESTE TAG ──────────────────────────────────
 *
 * `VeredictoRTO` no cambia. Un camión parado en el portón pidiendo salir está,
 * por definición, en uso: si el tag llegara hasta ahí, la unidad que estuvo seis
 * meses quieta saldría con la revisión vencida el día que alguien la arranca —
 * que es exactamente el día en que hay que frenarla.
 */
export const DIAS_SIN_USO = 60;

/* El piso de cobertura que enciende la regla.
 *
 * Si las fuentes de actividad se caen —nadie carga movimientos, la telemetría
 * deja de escribir, portería no sincroniza— todas las unidades pasarían a
 * «hace más de 60 días» a la vez y la lista de vencidas se vaciaría sola. Nadie
 * vería un error: vería una pantalla en verde, que es la peor forma de fallar.
 *
 * Con menos de esto la regla se APAGA y todo vuelve a «Vencido». Hoy hay 732
 * unidades vistas en la ventana, así que el piso no roza el caso normal ni de
 * lejos — está para el día en que algo se rompa. Es la misma idea que el
 * `sin_dato` de acá abajo: no poder mirar no es haber mirado. */
export const MINIMO_VISTAS = 50;

/* CUATRO GRUPOS Y NO TRES. El cuarto es el que se olvida siempre:
 *
 *   vencido    · ya pasó la fecha
 *   por_vencer · le quedan 30 días o menos
 *   al_dia     · le queda más que eso
 *   sin_dato   · **no lo sabemos**
 *
 * «Sin dato» no es «al día», y meterlo con los buenos es declarar en regla a
 * una unidad que nadie miró. Es la misma regla que `Celda` en documentación y
 * que `isla.es_de_regimen`: el que no se pudo consultar va en su propio grupo,
 * y se cuenta.
 */
export function grupo(fila, hoy = mediodiaHoyAr()) {
  const d = fila?.dias;
  if (d === null || d === undefined) return "sin_dato";
  if (d < 0) {
    /* El quinto grupo se abre SÓLO acá adentro. Una unidad al día que hace un
     * año que no se mueve no es un problema del RTO, y sacarla de «Al día» para
     * meterla en un grupo de revisiones haría que el chip conteste una pregunta
     * que no es la suya. El tag existe para partir lo vencido, nada más. */
    const v = diasSinVerla(fila, hoy);
    return v !== null && v > DIAS_SIN_USO ? "sin_uso" : "vencido";
  }
  if (d <= DIAS_AVISO) return "por_vencer";
  return "al_dia";
}

/* Hace cuántos días que no se ve la unidad, o `null` si no hay registro.
 *
 * `null` y `0` tienen que poder distinguirse —«nunca la vimos» y «la vimos
 * hoy»— y por eso no devuelve un número grande ante la ausencia: un centinela
 * cae del lado de «sin uso» solo, que es justo el lado que no hay que elegir
 * cuando no se sabe.
 *
 * Al mediodía las dos puntas, como `shared/ubicacion`: restando fechas crudas,
 * un cambio de huso o un horario de verano corre la cuenta un día entero, y en
 * el borde de los 60 eso enciende y apaga el tag sin que nada haya cambiado. */
export function diasSinVerla(fila, hoy = mediodiaHoyAr()) {
  const f = fila?.visto_en;
  if (!f) return null;
  const d = new Date(String(f).slice(0, 10) + "T12:00:00");
  if (Number.isNaN(d.getTime())) return null;
  const m = new Date(hoy.getFullYear(), hoy.getMonth(), hoy.getDate(), 12, 0, 0);
  return Math.round((m - d) / 86400000);
}

/* ── LO QUE LE PEGA LA ACTIVIDAD A LAS FILAS, Y APAGA LA REGLA SI NO HAY ───
 *
 * Está acá y no en cada pantalla porque las dos —tractores y furgones— tienen
 * que apagar la regla por el MISMO motivo y en el mismo momento. Con el
 * guardrail escrito en cada una, el día que una se caiga la otra sigue
 * escondiendo vencidas y nadie lo nota.
 *
 * `actividad` es un `Map` de interno → fecha, del padrón que corresponda. Si no
 * llegó (todavía carga, o falló la consulta) las filas vuelven TAL CUAL: sin
 * `visto_en` nadie cae en «Sin uso?» y la pantalla dice lo de siempre. Una
 * pantalla a medio cargar no puede empezar a esconder filas.
 */
export function conActividad(filas, actividad, hoy = mediodiaHoyAr()) {
  const fs = filas || [];
  if (!actividad || !hayCobertura(actividad, hoy)) return fs;
  return fs.map((f) => {
    const v = actividad.get(String(f?.interno));
    return v ? { ...f, visto_en: v } : f;
  });
}

/* ¿Las fuentes de actividad están vivas? Ver `MINIMO_VISTAS`. */
export function hayCobertura(actividad, hoy = mediodiaHoyAr()) {
  if (!actividad || typeof actividad.values !== "function") return false;
  let vistas = 0;
  for (const f of actividad.values()) {
    const d = diasSinVerla({ visto_en: f }, hoy);
    if (d !== null && d <= DIAS_SIN_USO && ++vistas >= MINIMO_VISTAS) return true;
  }
  return false;
}

/* El orden en que se ofrecen: primero lo que hay que resolver. Un filtro que
 * arranca por «al día» pondría adelante justamente lo que no hay que mirar. */
export const FILTROS = [
  ["todos", "Todos"],
  ["vencido", "Vencido"],
  /* Justo detrás de «Vencido» y no al final con «Sin dato»: es de donde salen
   * sus filas, y el que ve el número de vencidas más chico que la semana
   * pasada tiene que encontrar a dónde se fueron sin ir a buscarlo. */
  ["sin_uso", "Sin uso?"],
  ["por_vencer", `Vence en menos de ${DIAS_AVISO} días`],
  ["al_dia", "Al día"],
  ["sin_dato", "Sin dato"],
];

/* Cuántas hay de cada grupo. Se devuelven TODAS las claves aunque estén en
 * cero: un chip que aparece y desaparece según el día hace que la pantalla se
 * sienta distinta cada vez que se abre, y el que buscaba «Vencido» no lo
 * encuentra justo el día que no hay ninguno — que es el día en que más quiere
 * confirmarlo. */
export function contar(filas, hoy = mediodiaHoyAr()) {
  const c = { todos: (filas || []).length, vencido: 0, sin_uso: 0, por_vencer: 0,
              al_dia: 0, sin_dato: 0 };
  for (const f of filas || []) c[grupo(f, hoy)] += 1;
  return c;
}

export function filtrar(filas, cual, hoy = mediodiaHoyAr()) {
  if (!cual || cual === "todos") return filas || [];
  return (filas || []).filter((f) => grupo(f, hoy) === cual);
}

/* ── CÓMO SE DICE UNA FILA DE RTO ─────────────────────────────────────────
 *
 * Vivían adentro de `Furgones.jsx`. Al pasar tractores a la misma tabla, la
 * alternativa era copiarlas — y ahí las dos pantallas empiezan a decir cosas
 * distintas del mismo dato sin que nada falle: alcanza con que alguien toque
 * una. Es la razón por la que `VeredictoRTO` salió de adentro de `RTO.jsx`.
 *
 * Y el RTO de tractores decía lo MISMO escrito de otra forma —«vencido hace 3
 * días» en minúscula y en una línea de meta— así que además de dejar de
 * duplicarse, ahora los dos listados se leen igual. */

export const ETIQUETA = {
  vigente: "Al día",
  /* NO SE FUNDE CON `vigente`, y es la misma decisión que la prórroga de 60
   * días del registro: las dos dejan salir al camión, pero una la confirmó el
   * organismo y la otra la escribió una persona mirando el título de un 0 km.
   * Fundidas, no habría forma de contestar «¿cuántas de las que figuran al día
   * las verificó alguien?» — y ésa es justo la lista que hay que mirar el día
   * que el CENT empiece a contestar por esas patentes. */
  vigente_declarada: "Al día (del título)",
  por_vencer: "Por vencer",
  vencida: "Vencido",
  /* El signo de pregunta es el tag entero. «Sin uso» afirma algo que nadie
   * verificó —lo único que sabemos es que hace 60 días que no la vemos, que no
   * es lo mismo— y afirmado se archiva; preguntado, alguien lo va a mirar, que
   * es literalmente lo que se pidió: «que Nico o alguien lo vaya a ver y nos
   * diga qué onda». */
  sin_uso: "Sin uso?",
  sin_rto: "Sin RTO registrada",
  sin_consultar: "Sin consultar",
  sin_patente: "Sin patente cargada",
};

/* Cómo se dice el estado DE ESTA FILA, que no siempre es el que trae el
 * servidor.
 *
 * `estado` viene del CENT y contesta una sola cosa: si la revisión está
 * vigente. «Sin uso?» no sale de ahí —sale de cruzarlo con la actividad— así
 * que el servidor nunca lo va a mandar, y una pantalla que lea `ETIQUETA`
 * directo mostraría «Vencido» en la columna con el chip puesto en «Sin uso?».
 * Filtrar por un tag y que después ninguna fila lo diga es cómo se pierde la
 * confianza en un filtro.
 *
 * `ETIQUETA` queda como está y sigue siendo la traducción cruda del estado: la
 * usa el veredicto del portón, que a propósito NO conoce este tag. */
export function etiquetaDe(fila, hoy = mediodiaHoyAr()) {
  const g = grupo(fila, hoy);
  if (g === "sin_uso") return ETIQUETA.sin_uso;
  return ETIQUETA[fila?.estado] || fila?.estado;
}

/* El color del puntito, por la misma razón que `etiquetaDe`.
 *
 * El punto sale del `estado` del servidor, que para una fila «Sin uso?» sigue
 * diciendo `vencida` — o sea rojo. Un rojo al lado de «Sin uso?» se contradice
 * solo: el tag existe justamente para sacar del rojo lo que no es la tarea de
 * esta semana, y dejarlo rojo devuelve el problema entero.
 *
 * Va al gris de `sin_consultar`, que es el color que este hub ya usa para «no
 * lo sabemos». Y es lo que es: no sabemos si esa unidad se usa. */
export function puntoDe(fila, hoy = mediodiaHoyAr()) {
  const g = grupo(fila, hoy);
  return g === "sin_uso" ? "sin_uso" : fila?.estado || "vigente";
}

export function fecha(f) {
  if (!f) return "—";
  const [a, m, d] = String(f).slice(0, 10).split("-");
  return `${d}/${m}/${a}`;
}

/* «vence en 12 d» / «hace 3 d». El número solo no dice de qué lado del cero
 * está, y eso es justamente la información. */
export function cuando(f) {
  if (f.dias === null || f.dias === undefined) return "—";
  if (f.dias < 0) return `hace ${Math.abs(f.dias)} d`;
  if (f.dias === 0) return "hoy";
  return `en ${f.dias} d`;
}
